/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Interactive Live Preview Mockup for Chatbot Widget Studio (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useWidgetStudioStore } from '../../stores/useWidgetStudioStore';
import { useI18n } from '../../hooks/useI18n';
import {
  Monitor,
  Smartphone,
  RotateCcw,
  Send,
  X,
  MessageSquare,
  Sparkles,
  Bot,
  User,
  Lock,
} from 'lucide-react';

export const WidgetLivePreview: React.FC = () => {
  const { t } = useI18n();
  const {
    config,
    previewDevice,
    previewWidgetOpen,
    testMessages,
    isSimulatingResponse,
    setPreviewDevice,
    setPreviewWidgetOpen,
    togglePreviewWidget,
    sendTestMessage,
    resetTestChat,
  } = useWidgetStudioStore();

  const { appearance, starterQuestions, leadCapture } = config;
  const [inputText, setInputText] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(!leadCapture.enabled);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim() || isSimulatingResponse) return;
    const text = inputText;
    setInputText('');
    await sendTestMessage(text);
  };

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLeadSubmitted(true);
  };

  const isBottomRight = appearance.position === 'bottom-right';

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* 1. Preview Top Controls */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-1.5 p-1 rounded-xl border border-surface-border bg-surface-elevated/70">
          <button
            type="button"
            onClick={() => setPreviewDevice('desktop')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              previewDevice === 'desktop'
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            <Monitor className="w-3.5 h-3.5" />
            <span>{t('widget.desktopPreview')}</span>
          </button>
          <button
            type="button"
            onClick={() => setPreviewDevice('mobile')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              previewDevice === 'mobile'
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>{t('widget.mobilePreview')}</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              resetTestChat();
              if (leadCapture.enabled) setLeadSubmitted(false);
            }}
            className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground px-2.5 py-1 rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated transition-colors"
            title={t('widget.resetDemoBtn')}
          >
            <RotateCcw className="w-3 h-3" />
            <span>{t('widget.resetDemoBtn')}</span>
          </button>
        </div>
      </div>

      {/* 2. Device Mockup Container */}
      <div className="flex-1 min-h-[560px] rounded-2xl border border-surface-border bg-surface-elevated/40 p-4 sm:p-6 flex items-center justify-center overflow-hidden relative shadow-inner">
        {/* DESKTOP BROWSER FRAME */}
        {previewDevice === 'desktop' ? (
          <div className="w-full max-w-2xl h-[560px] rounded-2xl border border-surface-border bg-surface-elevated flex flex-col shadow-2xl overflow-hidden relative">
            {/* Browser Header Bar */}
            <div className="h-9 bg-muted/80 border-b border-surface-border flex items-center px-4 justify-between select-none">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              </div>
              <div className="flex items-center gap-1.5 px-3 py-0.5 rounded-md bg-surface border border-surface-border text-[10px] font-mono text-foreground-muted">
                <Lock className="w-2.5 h-2.5 text-emerald-400" />
                <span>https://yourcompany.com</span>
              </div>
              <div className="w-8" />
            </div>

            {/* Mock Website Canvas */}
            <div className="flex-1 bg-gradient-to-b from-surface to-surface-elevated p-6 overflow-y-auto relative select-none">
              <div className="space-y-4 max-w-md">
                <div className="h-6 w-32 rounded-lg bg-muted/80 animate-pulse" />
                <div className="space-y-2">
                  <div className="h-8 w-4/5 rounded-xl bg-muted/60" />
                  <div className="h-4 w-full rounded-lg bg-muted/40" />
                  <div className="h-4 w-2/3 rounded-lg bg-muted/40" />
                </div>
                <div className="flex gap-3 pt-2">
                  <div className="h-9 w-28 rounded-xl bg-primary/30 border border-primary/40" />
                  <div className="h-9 w-24 rounded-xl bg-muted/60" />
                </div>
              </div>

              {/* FLOATING CHATBOT WIDGET (DESKTOP) */}
              <div
                className={`absolute bottom-5 ${
                  isBottomRight ? 'right-5' : 'left-5'
                } z-20 flex flex-col items-${isBottomRight ? 'end' : 'start'} gap-3`}
              >
                {/* Chat Window Panel */}
                {previewWidgetOpen && (
                  <div className="w-80 sm:w-96 h-[440px] rounded-2xl border border-surface-border bg-surface shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {/* Widget Header */}
                    <div
                      className="p-3.5 text-white flex items-center justify-between flex-shrink-0 shadow-sm"
                      style={{ backgroundColor: appearance.primaryColor }}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-white/20 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="leading-tight">
                          <h4 className="font-bold text-xs">{appearance.botName}</h4>
                          <p className="text-[10px] text-white/80">{appearance.botSubtitle}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setPreviewWidgetOpen(false)}
                          className="w-6 h-6 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Widget Content Area */}
                    <div className="flex-1 p-3.5 overflow-y-auto space-y-3 text-xs bg-surface/50">
                      {/* Lead Capture Form Mode */}
                      {!leadSubmitted && leadCapture.enabled ? (
                        <form onSubmit={handleLeadSubmit} className="space-y-3 p-3 rounded-xl border border-surface-border bg-surface-elevated/70">
                          <div className="space-y-1">
                            <h5 className="font-bold text-foreground text-xs">{leadCapture.title}</h5>
                            <p className="text-[11px] text-foreground-muted">{leadCapture.subtitle}</p>
                          </div>

                          {leadCapture.requireName && (
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-foreground">{t('widget.fieldNameLabel')}</label>
                              <input
                                type="text"
                                required
                                value={leadName}
                                onChange={(e) => setLeadName(e.target.value)}
                                placeholder="e.g. John Doe"
                                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none"
                              />
                            </div>
                          )}

                          {leadCapture.requireEmail && (
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-foreground">{t('widget.fieldEmailLabel')}</label>
                              <input
                                type="email"
                                required
                                value={leadEmail}
                                onChange={(e) => setLeadEmail(e.target.value)}
                                placeholder="name@company.com"
                                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none"
                              />
                            </div>
                          )}

                          {leadCapture.requirePhone && (
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-foreground">{t('widget.fieldPhoneLabel')}</label>
                              <input
                                type="tel"
                                value={leadPhone}
                                onChange={(e) => setLeadPhone(e.target.value)}
                                placeholder="0901 234 567"
                                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none"
                              />
                            </div>
                          )}

                          <button
                            type="submit"
                            className="w-full py-2 text-xs font-bold rounded-lg text-white shadow-sm transition-opacity hover:opacity-90 mt-2"
                            style={{ backgroundColor: appearance.primaryColor }}
                          >
                            {leadCapture.submitButtonText}
                          </button>
                        </form>
                      ) : (
                        /* Normal Chat Conversation Mode */
                        <>
                          {testMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              {msg.role === 'bot' && (
                                <div
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] flex-shrink-0"
                                  style={{ backgroundColor: appearance.primaryColor }}
                                >
                                  <Bot className="w-3.5 h-3.5" />
                                </div>
                              )}
                              <div
                                className={`p-2.5 rounded-2xl max-w-[82%] text-xs leading-relaxed ${
                                  msg.role === 'user'
                                    ? 'bg-primary text-primary-fg rounded-br-none shadow-sm'
                                    : 'bg-surface-elevated text-foreground border border-surface-border rounded-bl-none'
                                }`}
                                style={
                                  msg.role === 'user'
                                    ? { backgroundColor: appearance.primaryColor }
                                    : undefined
                                }
                              >
                                {msg.content}
                              </div>
                              {msg.role === 'user' && (
                                <div className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] flex items-center justify-center flex-shrink-0">
                                  <User className="w-3.5 h-3.5" />
                                </div>
                              )}
                            </div>
                          ))}

                          {isSimulatingResponse && (
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] flex-shrink-0"
                                style={{ backgroundColor: appearance.primaryColor }}
                              >
                                <Bot className="w-3.5 h-3.5" />
                              </div>
                              <div className="p-2.5 rounded-2xl bg-surface-elevated border border-surface-border flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce" />
                                <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce [animation-delay:0.2s]" />
                                <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce [animation-delay:0.4s]" />
                              </div>
                            </div>
                          )}

                          {/* Quick Starter Prompts Chips */}
                          {testMessages.length <= 2 && starterQuestions.length > 0 && (
                            <div className="pt-2 space-y-1.5">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-accent" />
                                <span>{t('widget.starterQuestionsHeading')}:</span>
                              </span>
                              <div className="flex flex-col gap-1.5">
                                {starterQuestions.map((sq) => (
                                  <button
                                    key={sq.id}
                                    type="button"
                                    onClick={() => sendTestMessage(sq.question)}
                                    className="text-left px-2.5 py-1.5 rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-[11px] text-foreground transition-colors hover:border-primary/40"
                                  >
                                    {sq.question}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Widget Footer Input */}
                    {(leadSubmitted || !leadCapture.enabled) && (
                      <form
                        onSubmit={handleSendMessage}
                        className="p-2 border-t border-surface-border bg-surface flex items-center gap-1.5 flex-shrink-0"
                      >
                        <input
                          type="text"
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          placeholder={t('chat.inputPlaceholder')}
                          className="flex-1 px-3 py-1.5 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none"
                        />
                        <button
                          type="submit"
                          disabled={!inputText.trim() || isSimulatingResponse}
                          className="w-8 h-8 rounded-xl flex items-center justify-center text-white disabled:opacity-40 transition-opacity flex-shrink-0"
                          style={{ backgroundColor: appearance.primaryColor }}
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      </form>
                    )}
                  </div>
                )}

                {/* Floating Launcher Button */}
                <div className="flex items-center gap-2">
                  {!previewWidgetOpen && appearance.launcherText && (
                    <span className="px-3 py-1.5 rounded-xl border border-surface-border bg-surface text-xs font-semibold text-foreground shadow-lg animate-in fade-in slide-in-from-bottom-2">
                      {appearance.launcherText}
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={togglePreviewWidget}
                    className="w-13 h-13 rounded-full text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 flex-shrink-0"
                    style={{ backgroundColor: appearance.primaryColor }}
                    aria-label="Toggle chatbot preview"
                  >
                    {previewWidgetOpen ? (
                      <X className="w-6 h-6" />
                    ) : (
                      <MessageSquare className="w-6 h-6" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* MOBILE SMARTPHONE FRAME */
          <div className="w-[320px] h-[540px] rounded-[38px] border-4 border-surface-border bg-surface-elevated flex flex-col shadow-2xl overflow-hidden relative">
            {/* Dynamic Island Notch */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-4 rounded-full bg-black z-30 flex items-center justify-end pr-2">
              <span className="w-1.5 h-1.5 rounded-full bg-surface-border" />
            </div>

            {/* Mobile Header Bar */}
            <div
              className="pt-8 pb-3 px-4 text-white flex items-center justify-between flex-shrink-0 shadow-sm z-20"
              style={{ backgroundColor: appearance.primaryColor }}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-white/20 text-white font-bold text-xs flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5" />
                </div>
                <div className="leading-tight">
                  <h4 className="font-bold text-xs truncate max-w-[150px]">{appearance.botName}</h4>
                  <p className="text-[9px] text-white/80">{appearance.botSubtitle}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={resetTestChat}
                className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white"
                title={t('widget.resetDemoBtn')}
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            </div>

            {/* Mobile Chat Stream Area */}
            <div className="flex-1 p-3 overflow-y-auto space-y-2.5 text-xs bg-surface/90 text-foreground">
              {testMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-1.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`p-2.5 rounded-2xl max-w-[85%] text-[11px] leading-relaxed ${
                      msg.role === 'user'
                        ? 'text-white rounded-br-none'
                        : 'bg-surface-elevated text-foreground border border-surface-border rounded-bl-none'
                    }`}
                    style={
                      msg.role === 'user'
                        ? { backgroundColor: appearance.primaryColor }
                        : undefined
                    }
                  >
                    {msg.content}
                  </div>
                </div>
              ))}

              {isSimulatingResponse && (
                <div className="flex items-center gap-1.5 p-2 rounded-xl bg-surface-elevated border border-surface-border w-16">
                  <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce [animation-delay:0.2s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce [animation-delay:0.4s]" />
                </div>
              )}
            </div>

            {/* Mobile Footer Input */}
            <form
              onSubmit={handleSendMessage}
              className="p-2 border-t border-surface-border bg-surface-elevated flex items-center gap-1.5 flex-shrink-0"
            >
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={t('chat.inputPlaceholder')}
                className="flex-1 px-3 py-1.5 text-xs rounded-full border border-surface-border bg-surface text-foreground focus:outline-none"
              />
              <button
                type="submit"
                disabled={!inputText.trim() || isSimulatingResponse}
                className="w-7 h-7 rounded-full flex items-center justify-center text-white disabled:opacity-40 flex-shrink-0"
                style={{ backgroundColor: appearance.primaryColor }}
              >
                <Send className="w-3 h-3" />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
